import React, { useState, useMemo } from 'react';
import { 
  Target, 
  TrendingUp, 
  DollarSign, 
  ChevronDown, 
  ChevronUp,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Map,
  ListChecks,
  Calendar,
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
  Zap,
  Info
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { formatCurrency } from '../lib/utils';
import { useApp } from '../context/AppContext';
import {
  getValueForPercentile,
  calculateRequiredLei,
  getLeiTier,
} from '../lib/benchmarks';
import { exportRoadmapPDF, exportRoadmapExcel } from '../lib/api';
import logger from '../lib/logger';

// Calculate composite score: Impact × Achievability
const calculateCompositeScore = (measure, targetValue, currentValue, currentLei) => {
  // Impact score: based on measure weight and elasticity
  const weight = measure.weight || 0.5;
  const elasticity = measure.elasticity || 1.0;
  const impactScore = weight * elasticity * 10;
  
  // Achievability score: based on gap size (smaller gap = more achievable)
  const requiredLei = calculateRequiredLei(currentLei, currentValue, targetValue);
  const leiIncrease = Math.max(0, requiredLei - currentLei);
  // Normalize: 0-20% increase = high achievability, 20-50% = medium, 50%+ = low
  const achievabilityScore = Math.max(0, 10 - (leiIncrease / 5));
  
  // Composite = Impact × Achievability (both weighted equally)
  const composite = (impactScore * 0.5 + achievabilityScore * 0.5);
  
  return {
    impactScore: impactScore.toFixed(1),
    achievabilityScore: achievabilityScore.toFixed(1),
    compositeScore: composite.toFixed(1),
    requiredLei,
    leiIncrease,
  };
};

// Roadmap item component
const RoadmapItem = ({ 
  item, 
  index, 
  isSelected, 
  onToggle, 
  onCostChange, 
  viewMode 
}) => {
  const priorityColors = {
    high: 'border-emerald-300 bg-emerald-50',
    medium: 'border-amber-300 bg-amber-50',
    low: 'border-slate-300 bg-slate-50',
  };
  
  const priority = item.compositeScore >= 7 ? 'high' : item.compositeScore >= 4 ? 'medium' : 'low';
  
  if (viewMode === 'timeline') {
    return (
      <div className={`relative pl-8 pb-6 ${index === 0 ? '' : 'border-l-2 border-purple-200 ml-3'}`}>
        <div className={`absolute left-0 top-0 w-6 h-6 rounded-full flex items-center justify-center ${
          isSelected ? 'bg-purple-600 text-white' : 'bg-white border-2 border-purple-300'
        }`}>
          {isSelected ? <CheckCircle2 className="h-4 w-4" /> : <span className="text-xs font-medium">{index + 1}</span>}
        </div>
        <div className={`ml-4 p-3 rounded-lg border ${priorityColors[priority]}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-slate-800">{item.measure.name}</span>
            <Badge variant="outline" className="text-[10px]">
              Score: {item.compositeScore}
            </Badge>
          </div>
          <div className="text-xs text-slate-600 space-y-1">
            <p>Current: {item.currentLei.toFixed(0)}% → Target: {item.requiredLei.toFixed(0)}%</p>
            <p className="text-purple-600">+{item.leiIncrease.toFixed(0)}% improvement needed</p>
          </div>
          {isSelected && (
            <div className="mt-2 pt-2 border-t border-slate-200">
              <Label className="text-[10px] text-slate-500">Est. Cost ($)</Label>
              <Input
                type="number"
                value={item.cost || ''}
                onChange={(e) => onCostChange(item.measure.id, parseFloat(e.target.value) || 0)}
                className="h-7 text-xs mt-1"
                placeholder="Enter cost..."
              />
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // List view
  return (
    <div className={`p-3 rounded-lg border ${isSelected ? 'border-purple-400 bg-purple-50' : priorityColors[priority]}`}>
      <div className="flex items-start gap-3">
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggle(item.measure.id)}
          className="mt-1"
        />
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <span className="font-medium text-slate-800">{item.measure.name}</span>
            <div className="flex items-center gap-2">
              <Badge 
                variant="outline" 
                className={`text-[9px] ${
                  priority === 'high' ? 'border-emerald-400 text-emerald-700' :
                  priority === 'medium' ? 'border-amber-400 text-amber-700' :
                  'border-slate-400 text-slate-600'
                }`}
              >
                {priority === 'high' ? 'High Priority' : priority === 'medium' ? 'Medium' : 'Lower Priority'}
              </Badge>
              <span className="text-xs text-slate-500">Score: {item.compositeScore}</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 text-xs text-slate-600 mt-2">
            <div>
              <span className="text-slate-400">Impact:</span>
              <span className="ml-1 font-medium">{item.impactScore}/10</span>
            </div>
            <div>
              <span className="text-slate-400">Achievability:</span>
              <span className="ml-1 font-medium">{item.achievabilityScore}/10</span>
            </div>
            <div>
              <span className="text-slate-400">LEI Change:</span>
              <span className="ml-1 font-medium text-purple-600">+{item.leiIncrease.toFixed(0)}%</span>
            </div>
          </div>
          {isSelected && (
            <div className="mt-3 pt-2 border-t border-slate-200 flex items-center gap-3">
              <Label className="text-xs text-slate-500 whitespace-nowrap">Est. Cost:</Label>
              <div className="relative flex-1">
                <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                <Input
                  type="number"
                  value={item.cost || ''}
                  onChange={(e) => onCostChange(item.measure.id, parseFloat(e.target.value) || 0)}
                  className="h-7 text-xs pl-6"
                  placeholder="0"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const InterventionRoadmap = ({ 
  measures = [], 
  goalCalculations,
  benchmarks,
  currentLei,
  roiGoalPercentile,
  kpiGoalPercentile,
  paybackGoalPercentile
}) => {
  const { currency } = useApp();
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'timeline'
  const [selectedMeasures, setSelectedMeasures] = useState({});
  const [measureCosts, setMeasureCosts] = useState({});
  const [sortBy, setSortBy] = useState('composite'); // 'composite', 'impact', 'achievability'
  
  // Calculate scores for all measures
  const scoredMeasures = useMemo(() => {
    if (!measures.length || !goalCalculations) return [];
    
    const targetRoi = getValueForPercentile(roiGoalPercentile, benchmarks.roi);
    const currentRoi = goalCalculations.roi.current;
    
    return measures.map(measure => {
      const scores = calculateCompositeScore(
        measure,
        targetRoi,
        currentRoi,
        currentLei
      );
      
      return {
        measure,
        currentLei,
        ...scores,
        cost: measureCosts[measure.id] || 0,
      };
    });
  }, [measures, goalCalculations, benchmarks, currentLei, roiGoalPercentile, measureCosts]);

  // Sort measures separately to avoid re-computing scores on sort change
  const rankedMeasures = useMemo(() => {
    return [...scoredMeasures].sort((a, b) => {
      switch (sortBy) {
        case 'impact':
          return parseFloat(b.impactScore) - parseFloat(a.impactScore);
        case 'achievability':
          return parseFloat(b.achievabilityScore) - parseFloat(a.achievabilityScore);
        default:
          return parseFloat(b.compositeScore) - parseFloat(a.compositeScore);
      }
    });
  }, [scoredMeasures, sortBy]);
  
  // Toggle measure selection
  const toggleMeasure = (measureId) => {
    setSelectedMeasures(prev => ({
      ...prev,
      [measureId]: !prev[measureId]
    }));
  };
  
  // Update cost for a measure
  const updateCost = (measureId, cost) => {
    setMeasureCosts(prev => ({
      ...prev,
      [measureId]: cost
    }));
  };
  
  // Calculate roadmap totals
  const roadmapTotals = useMemo(() => {
    const selectedItems = rankedMeasures.filter(item => selectedMeasures[item.measure.id]);
    const totalCost = selectedItems.reduce((sum, item) => sum + (measureCosts[item.measure.id] || 0), 0);
    const avgLeiIncrease = selectedItems.length > 0 
      ? selectedItems.reduce((sum, item) => sum + item.leiIncrease, 0) / selectedItems.length
      : 0;
    
    // Estimate projected ROI based on average LEI increase
    const projectedLei = currentLei + avgLeiIncrease;
    const projectedRoiMultiplier = 1 + (avgLeiIncrease / 100) * 2.5; // Simplified model
    const projectedFinancialImpact = goalCalculations?.roi?.current 
      ? (goalCalculations.roi.current / 100) * projectedRoiMultiplier * 1000 * projectedLei
      : 0;
    const netBenefit = projectedFinancialImpact - totalCost;
    const roadmapRoi = totalCost > 0 ? ((netBenefit / totalCost) * 100) : 0;
    
    return {
      selectedCount: selectedItems.length,
      totalCost,
      avgLeiIncrease,
      projectedLei,
      projectedFinancialImpact,
      netBenefit,
      roadmapRoi,
      selectedItems,
    };
  }, [rankedMeasures, selectedMeasures, measureCosts, currentLei, goalCalculations]);
  
  // Export loading state
  const [exportLoading, setExportLoading] = useState(false);
  
  // Export roadmap data
  const handleExport = async (format = 'pdf') => {
    const exportData = {
      roadmap: roadmapTotals.selectedItems.map((item, idx) => ({
        priority: idx + 1,
        measure: item.measure.name,
        currentLei: item.currentLei,
        targetLei: item.requiredLei,
        leiIncrease: item.leiIncrease,
        impactScore: item.impactScore,
        achievabilityScore: item.achievabilityScore,
        compositeScore: item.compositeScore,
        estimatedCost: measureCosts[item.measure.id] || 0,
      })),
      totals: {
        totalInterventions: roadmapTotals.selectedCount,
        totalInvestment: roadmapTotals.totalCost,
        avgLeiIncrease: roadmapTotals.avgLeiIncrease,
        projectedRoi: roadmapTotals.roadmapRoi,
        netBenefit: roadmapTotals.netBenefit,
      },
      goals: {
        roiPercentile: roiGoalPercentile,
        kpiPercentile: kpiGoalPercentile,
        paybackPercentile: paybackGoalPercentile,
      },
      include_charts: true,
      include_branding: true,
      report_title: 'Intervention Roadmap Report'
    };
    
    setExportLoading(true);
    try {
      let response;
      let filename;
      let mimeType;
      
      if (format === 'pdf') {
        response = await exportRoadmapPDF(exportData);
        filename = `Intervention_Roadmap_${new Date().toISOString().slice(0, 10)}.pdf`;
        mimeType = 'application/pdf';
      } else {
        response = await exportRoadmapExcel(exportData);
        filename = `Intervention_Roadmap_${new Date().toISOString().slice(0, 10)}.xlsx`;
        mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      }
      
      // Trigger download
      const blob = new Blob([response.data], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      logger.error('Export failed:', error);
      alert('Failed to export roadmap. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };
  
  if (!measures.length) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-8 text-center">
          <Map className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No learning measures available for roadmap planning.</p>
          <p className="text-xs text-slate-400 mt-1">Run an analysis first to see intervention recommendations.</p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="border-purple-200 bg-gradient-to-br from-white to-purple-50/20">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Map className="h-5 w-5 text-purple-600" />
              Intervention Roadmap
            </CardTitle>
            <CardDescription>
              Prioritized learning interventions to achieve your goals
            </CardDescription>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-4 w-4 text-slate-400" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-xs">
                  Measures are ranked by a composite score combining Impact (weight × elasticity) 
                  and Achievability (inverse of required LEI change). Higher scores indicate 
                  better candidates for goal achievement.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* View Mode & Sort Controls */}
        <div className="flex items-center justify-between">
          <Tabs value={viewMode} onValueChange={setViewMode} className="w-auto">
            <TabsList className="h-8">
              <TabsTrigger value="list" className="text-xs px-3 h-6">
                <ListChecks className="h-3 w-3 mr-1" />
                List
              </TabsTrigger>
              <TabsTrigger value="timeline" className="text-xs px-3 h-6">
                <Calendar className="h-3 w-3 mr-1" />
                Timeline
              </TabsTrigger>
            </TabsList>
          </Tabs>
          
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">Sort by:</Label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-xs border rounded px-2 py-1 bg-white"
            >
              <option value="composite">Composite Score</option>
              <option value="impact">Impact Potential</option>
              <option value="achievability">Achievability</option>
            </select>
          </div>
        </div>
        
        {/* Measures List/Timeline */}
        <div className={`space-y-${viewMode === 'timeline' ? '0' : '2'} max-h-[400px] overflow-y-auto pr-2`}>
          {rankedMeasures.map((item, index) => (
            <RoadmapItem
              key={item.measure.id}
              item={item}
              index={index}
              isSelected={selectedMeasures[item.measure.id] || false}
              onToggle={toggleMeasure}
              onCostChange={updateCost}
              viewMode={viewMode}
            />
          ))}
        </div>
        
        {/* Roadmap Summary */}
        {roadmapTotals.selectedCount > 0 && (
          <div className="p-4 bg-gradient-to-r from-purple-100 to-indigo-100 rounded-lg border border-purple-200">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-5 w-5 text-purple-600" />
              <h4 className="font-semibold text-slate-800">Roadmap Investment Summary</h4>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-white rounded-lg">
                <p className="text-[10px] text-slate-500 uppercase">Interventions</p>
                <p className="text-xl font-bold text-slate-800">{roadmapTotals.selectedCount}</p>
              </div>
              <div className="p-3 bg-white rounded-lg">
                <p className="text-[10px] text-slate-500 uppercase">Total Investment</p>
                <p className="text-xl font-bold text-slate-800">{formatCurrency(roadmapTotals.totalCost, currency)}</p>
              </div>
              <div className="p-3 bg-white rounded-lg">
                <p className="text-[10px] text-slate-500 uppercase">Avg LEI Boost</p>
                <p className="text-xl font-bold text-purple-600">+{roadmapTotals.avgLeiIncrease.toFixed(0)}%</p>
              </div>
              <div className="p-3 bg-white rounded-lg">
                <p className="text-[10px] text-slate-500 uppercase">Roadmap ROI</p>
                <p className={`text-xl font-bold ${roadmapTotals.roadmapRoi > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {roadmapTotals.roadmapRoi > 0 ? '+' : ''}{roadmapTotals.roadmapRoi.toFixed(0)}%
                </p>
              </div>
            </div>
            
            {/* Net Benefit Calculation */}
            <div className="mt-4 p-3 bg-white rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Projected Financial Impact</p>
                  <p className="text-lg font-semibold text-slate-700">{formatCurrency(roadmapTotals.projectedFinancialImpact, currency)}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Total Investment</p>
                  <p className="text-lg font-semibold text-slate-700">-{formatCurrency(roadmapTotals.totalCost, currency)}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400" />
                <div className="text-right">
                  <p className="text-xs text-slate-500">Net Benefit</p>
                  <p className={`text-lg font-bold ${roadmapTotals.netBenefit > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {formatCurrency(roadmapTotals.netBenefit, currency)}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Target Achievement */}
            <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <Target className="h-4 w-4 text-purple-600" />
                <span className="text-sm font-medium text-purple-800">Goal Progress</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-600">Current LEI: {currentLei.toFixed(0)}%</span>
                    <span className="text-purple-600">Projected: {roadmapTotals.projectedLei.toFixed(0)}%</span>
                  </div>
                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-500 to-purple-600 rounded-full transition-all"
                      style={{ width: `${Math.min(100, roadmapTotals.projectedLei)}%` }}
                    />
                  </div>
                </div>
                <Badge className={`${
                  roadmapTotals.projectedLei >= goalCalculations?.overall?.maxRequiredLei 
                    ? 'bg-emerald-100 text-emerald-700' 
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {roadmapTotals.projectedLei >= (goalCalculations?.overall?.maxRequiredLei || 100)
                    ? 'Goal Achievable!'
                    : `${((roadmapTotals.projectedLei / (goalCalculations?.overall?.maxRequiredLei || 100)) * 100).toFixed(0)}% to Goal`
                  }
                </Badge>
              </div>
            </div>
            
            {/* Export Button */}
            <div className="mt-4 flex justify-end">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    className="bg-purple-600 hover:bg-purple-700"
                    disabled={exportLoading}
                    data-testid="export-roadmap-btn"
                  >
                    {exportLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    {exportLoading ? 'Exporting...' : 'Export Roadmap'}
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem 
                    onClick={() => handleExport('pdf')}
                    className="cursor-pointer"
                    data-testid="export-roadmap-pdf"
                  >
                    <FileText className="h-4 w-4 mr-2 text-rose-500" />
                    Export as PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => handleExport('excel')}
                    className="cursor-pointer"
                    data-testid="export-roadmap-excel"
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-500" />
                    Export as Excel
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        )}
        
        {roadmapTotals.selectedCount === 0 && (
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 text-center">
            <p className="text-sm text-slate-500">
              Select interventions above to build your roadmap and see investment analysis.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InterventionRoadmap;
